import React from 'react';
import { useParams } from 'react-router-dom';
import Seo from '../seo/Seo';
import { breadcrumbLd } from '../seo/jsonLd';
import Breadcrumb from './Breadcrumb';
import CTASection from './CTASection';
import NotFoundPage from './NotFoundPage';
import ReactMarkdown from 'react-markdown';
import { CheckCircle, Briefcase, TrendingUp, AlertTriangle, ShieldCheck, Settings, DollarSign, Zap } from 'lucide-react';

// Enhanced Story Structure
interface KeyIntervention {
  title: string;
  description: string;
  icon?: React.ElementType;
}

interface ResultHighlight {
  metric: string;
  label: string;
  icon?: React.ElementType;
}

type Practice = 'ai' | 'aws';

interface StoryContent {
  slug: string;
  practice: Practice;
  metaTitle: string;
  metaDescription: string;
  pageTitle: string;
  executiveSummary: string;
  clientInfo?: {
    name: string;
    industry: string;
    challengeSummary: string;
  };
  challengeSection: {
    title: string;
    content: string; // Markdown supported
  };
  solutionSection: {
    title: string;
    intro: string; // Markdown supported
    keyInterventions: KeyIntervention[];
  };
  resultsSection: {
    title: string;
    summary: string; // Markdown supported
    highlights: ResultHighlight[];
  };
}

const stories: StoryContent[] = [
  {
    slug: "afk-development",
    practice: "ai",
    metaTitle: "Case Study: An Overnight AFK Development Swarm | Kult.io",
    metaDescription: "How Kult.io built an away-from-keyboard development swarm. Technical plans become issues, agents implement them overnight behind checks they cannot skip, and every pull request still meets a human. Throughput up 70%.",
    pageTitle: "An Overnight Development Swarm, Built So It Can Say No",
    executiveSummary: "A developer writes a technical plan. It becomes an issue. Overnight a small swarm of agents picks it up, implements it, and opens a pull request. Development throughput went up 70%, and 85% of the code was agent-generated. The hard part was never the code generation. It was building gates the agent is not allowed to mark itself against.",
    clientInfo: {
      name: "Confidential",
      industry: "Software product development",
      challengeSummary: "Plans were becoming issues faster than the team could implement them, and coding agents produced more output than anyone could responsibly review."
    },
    challengeSection: {
      title: "The Challenge: Unattended Work You Can Still Trust",
      content: "The team already wrote good technical plans, and those plans already became issues. The bottleneck was everything after that.\n\nCoding agents could produce implementations. What nobody could produce was a reason to trust an implementation that nobody watched.\n\nThe repository made it harder. It was effectively greenfield, with no application code and no established stack, so there was nothing concrete to build a pipeline against. We designed the swarm against a **check contract** instead of a stack. Whichever of `lint`, `format`, `typecheck`, `test` and `test:e2e` a project defines, the runner runs. It skips the ones that are missing. The same pipeline works on day one of a repository and on year three of one."
    },
    solutionSection: {
      title: "The Design: The Runner Owns Every Gate",
      intro: "One principle decides the architecture. The runner owns every gate, not the agent. The agent never marks its own homework. Everything below follows from that:",
      keyInterventions: [
        {
          title: "A nightly schedule and a label, sharing one launcher",
          description: "Issues enter the swarm on a nightly run, or the moment a developer applies a label. A launcher enforces a hard concurrency cap and refuses to start a second worker for an issue already in flight. We started the cap at one, deliberately.",
          icon: Briefcase
        },
        {
          title: "A feedback loop the agent cannot skip",
          description: "The runner executes the project's checks and feeds every failure back into the session until they pass. The agent never decides it is finished. Passing checks decide. Without this the signal that code works arrives in CI minutes later, or from a tester days later. That is why unattended work usually fails.",
          icon: Zap
        },
        {
          title: "A review session with no memory of writing the code",
          description: "A second session opens on the same diff with fresh context and reviews it against a severity-ranked schema. Findings above a threshold go back into the implementation session. Then the whole check suite runs again from the top.",
          icon: ShieldCheck
        },
        {
          title: "Powers the agent is never given",
          description: "It cannot edit the orchestrator that runs it. It cannot push, merge, or change a remote. It never holds the credential the runner uses to talk to GitHub. It edits files and commits locally. Every remote operation belongs to the runner.",
          icon: AlertTriangle
        },
        {
          title: "Four separate ceilings on a single run",
          description: "Iterations, conversation turns, wall-clock time, and money. Any one of them ends the run. A swarm that cannot stop itself is not unattended, it is unsupervised.",
          icon: Settings
        },
        {
          title: "Not converging is a designed outcome",
          description: "When the loop cannot get to green, the worker pushes what it has, opens a draft pull request describing what it tried and what still fails, labels the issue blocked, and pings a human. It never opens a confident pull request it cannot support.",
          icon: CheckCircle
        }
      ]
    },
    resultsSection: {
      title: "Results, and How We Know They Are Real",
      summary: "Development throughput went up 70%, and 85% of the code was agent-generated. Only 15% was written by hand.\n\nThe test we care about most is a negative one. Label a deliberately vague issue and watch what happens. The expected result is a blocked issue and a draft pull request explaining the ambiguity. **If a vague issue turns into a clean pull request, the loop is not gating on anything.** It is producing confident output nobody should trust.\n\nEvery run writes its full transcript to object storage with tracing and structured logs, so a pull request that looks wrong can be read back to the decision that produced it. Workers never merge. Every pull request still meets a human."
      ,
      highlights: [
        {
          metric: "70%",
          label: "More Development Throughput",
          icon: TrendingUp
        },
        {
          metric: "85%",
          label: "Of the Code Agent-Generated",
          icon: CheckCircle
        },
        {
          metric: "100%",
          label: "Of Pull Requests Reviewed by a Human",
          icon: ShieldCheck
        }
      ]
    }
  },
  {
    slug: "cost-reduction",
    practice: "aws",
    metaTitle: "Case Study: AWS Cost Optimization - Kult.io",
    metaDescription: "Review how Kult.io provided a solution for a global application developer to reduce AWS costs by 60% through infrastructure optimization and improved monitoring.",
    pageTitle: "AWS Cost Reduction: A 60% Optimization for a Global Developer",
    executiveSummary: "A global application developer experienced significant AWS costs from their active development process, impacting business profitability. Kult.io provided a solution involving a multi-faceted optimization of their AWS infrastructure. This project resulted in a 60% reduction in operational costs and established a system for ongoing fiscal management in the cloud.",
    clientInfo: {
      name: "Global Application Developer",
      industry: "Software Development & Cloud Services",
      challengeSummary: "High AWS operational costs from extensive, multi-environment development work."
    },
    challengeSection: {
      title: "The Challenge: Managing Cloud Expenditures",
      content: "Our client, a global developer of applications, utilized multiple AWS environments for their development, staging, and production work. This active process, essential for their business, led to complex and substantial AWS service costs. These expenditures began to affect profits, a significant issue for the application's financial health. The company asked Kult.io to provide a solution: **reduce cloud service costs substantially, without affecting development time or system performance.**"
    },
    solutionSection: {
      title: "Our Solution: Infrastructure Optimization & Process Improvement",
      intro: "Our team conducted a detailed analysis of the client's AWS infrastructure. We identified key areas for optimization, focusing on structural improvements, service utilization, and financial management processes:",
      keyInterventions: [
        {
          title: "Unified AWS Organization & Service Management",
          description: "We consolidated all AWS environments under a single AWS Organization. This important step streamlined the management of shared AWS services (including AWS KMS) and allowed for centralized billing. This provided clearer information on service spend patterns.",
          icon: Briefcase
        },
        {
          title: "Intelligent Service Configuration & Scaling",
          description: "We strategically reconfigured development-related AWS managed services to on-demand models where data persistence was key. For other workloads, we implemented cost-effective EC2 instances, ensuring they were correctly sized to match demand and avoid unnecessary costs.",
          icon: Settings
        },
        {
          title: "Proactive Financial Management & AWS Savings Plans",
          description: "A full review of existing AWS Savings Plans was performed. We adjusted these to accurately reflect current and projected infrastructure usage, an area nobody had been monitoring. This change provided immediate cost benefits.",
          icon: DollarSign
        }
      ]
    },
    resultsSection: {
      title: "Project Outcomes: Significant Cost Reduction & Control",
      summary: "The implementation of this solution yielded measurable and sustained results, transforming the client's AWS cost structure and supporting their business growth:",
      highlights: [
        {
          metric: "60%",
          label: "Reduction in Monthly AWS Service Costs",
          icon: CheckCircle
        },
        {
          metric: "Improved",
          label: "Business Profitability from Application",
          icon: CheckCircle
        },
        {
          metric: "Effective",
          label: "Cost Management with Continuous Monitoring System",
          icon: CheckCircle
        }
      ]
    }
  },
  {
    slug: "enterprise-reliability",
    practice: "aws",
    metaTitle: "Case Study: Enterprise System Reliability (99.999% Uptime) - Kult.io",
    metaDescription: "Review how Kult.io resolved system downtime for a major enterprise, achieving 99.999% availability through deployment process optimization.",
    pageTitle: "System Reliability Project: Achieving 99.999% Uptime",
    executiveSummary: "A major enterprise, with significant annual revenue, had issues with persistent downtime on their key public-facing system. Kult.io developed and implemented a robust deployment process solution. This project resulted in 99.999% availability, securing company reputation and business operations.",
    clientInfo: {
      name: "Large Enterprise Company",
      industry: "Diversified Public Services",
      challengeSummary: "Daily system downtime on critical landing page, affecting sales and client information access."
    },
    challengeSection: {
      title: "The Issue: System Downtime and Business Impact",
      content: "The client's primary system, the access point for all public sites and key product information, had frequent outages. These incidents, occurring nearly every day for short periods, created problems: negative public perception, lost sales, and client frustration from inability to access important application information. The existing auto-scaled AWS environment was not performing under this pressure, requiring an immediate, expert solution."
    },
    solutionSection: {
      title: "Our Solution: Modernized Deployment Process for System Resilience",
      intro: "Our analysis identified key inefficiencies in the instance startup and deployment process within the auto-scaled environment. The main issue was a full application installation on every new instance, without a correctly configured auto-scaling grace period. Our solution involved several technical improvements:",
      keyInterventions: [
        {
          title: "Optimized Amazon Machine Images (AMIs)",
          description: "We implemented the use of AMIs with all application dependencies pre-installed. This greatly reduced instance launch times, providing a foundation for a rapid-response infrastructure.",
          icon: ShieldCheck
        },
        {
          title: "Automated Deployments with AWS CodeDeploy",
          description: "The application deployment process was re-engineered using AWS CodeDeploy. This ensured consistent, automated updates, reducing manual errors and improving overall deployment time and system reliability.",
          icon: Zap
        },
        {
          title: "Intelligent Auto-Scaling Group Configuration",
          description: "We recalibrated the auto-scaling group's health check grace period and scaling policies. This ensured new instances were fully operational before receiving traffic, preventing issues with unready instances.",
          icon: TrendingUp
        }
      ]
    },
    resultsSection: {
      title: "Project Outcomes: System Stability and Performance",
      summary: "This technical solution delivered significant improvements in system stability and operational performance for the company:",
      highlights: [
        {
          metric: "99.999%",
          label: "Sustained System Availability",
          icon: CheckCircle
        },
        {
          metric: "Zero",
          label: "Unplanned System Downtime Post-Implementation",
          icon: AlertTriangle
        },
        {
          metric: "Improved",
          label: "Client Access & Company Reputation",
          icon: CheckCircle
        }
      ]
    }
  }
];

interface SuccessStoryPageProps {
  onOpenContactModal: () => void;
}

const SuccessStoryPage: React.FC<SuccessStoryPageProps> = ({ onOpenContactModal }) => {
  const { slug } = useParams<{ slug: string }>();
  const story = stories.find(s => s.slug === slug);

  if (!story) {
    return <NotFoundPage />;
  }

  const crumbs = [
    { name: 'Home', path: '/' },
    { name: 'Case studies', path: '/#projects' },
    { name: story.pageTitle },
  ];

  const accentText = story.practice === 'ai' ? 'text-accent-400' : 'text-cloud-300';
  const proseClass =
    'prose prose-invert prose-lg max-w-measure text-gray-300 prose-p:mb-4 prose-strong:text-white prose-headings:font-display prose-headings:text-white';

  return (
    <div className="bg-gray-900 text-white">
      <Seo
        title={story.metaTitle}
        description={story.metaDescription}
        path={`/success-stories/${story.slug}`}
        type="article"
        jsonLd={breadcrumbLd(crumbs)}
      />

      <div className="border-b border-gray-800 bg-gray-950">
        <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 md:py-20">
          <h1 className="font-display text-display-md font-bold text-white sm:text-display-lg">
            {story.pageTitle}
          </h1>
          <p className="mt-6 max-w-measure text-lg text-gray-300">{story.executiveSummary}</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-16 sm:px-6 lg:px-8 md:py-20">
        <Breadcrumb crumbs={crumbs} />

        {story.clientInfo && (
          <section className="mt-12 border-t border-gray-800">
            <h2 className="sr-only">Engagement</h2>
            <dl className="grid gap-x-10 sm:grid-cols-3">
              {[
                ['Client', story.clientInfo.name],
                ['Industry', story.clientInfo.industry],
                ['Core challenge', story.clientInfo.challengeSummary],
              ].map(([term, value]) => (
                <div key={term} className="border-b border-gray-800 py-6">
                  <dt className="font-mono text-eyebrow uppercase text-gray-400">{term}</dt>
                  <dd className="mt-3 text-gray-200">{value}</dd>
                </div>
              ))}
            </dl>
          </section>
        )}

        <article className="mt-16 space-y-16 md:mt-20 md:space-y-20">
          <section className="grid gap-x-10 gap-y-6 lg:grid-cols-[1fr_2fr]">
            <h2 className="font-display text-display-sm font-bold text-white sm:text-display-md">
              {story.challengeSection.title}
            </h2>
            <div className={proseClass}>
              <ReactMarkdown>{story.challengeSection.content}</ReactMarkdown>
            </div>
          </section>

          <section>
            <div className="grid gap-x-10 gap-y-6 lg:grid-cols-[1fr_2fr]">
              <h2 className="font-display text-display-sm font-bold text-white sm:text-display-md">
                {story.solutionSection.title}
              </h2>
              <div className={proseClass}>
                <ReactMarkdown>{story.solutionSection.intro}</ReactMarkdown>
              </div>
            </div>

            <ol className="mt-12 border-t border-gray-800">
              {story.solutionSection.keyInterventions.map((item, index) => (
                <li
                  key={item.title}
                  className="grid gap-x-10 gap-y-3 border-b border-gray-800 py-8 lg:grid-cols-[1fr_2fr]"
                >
                  <div className="flex items-baseline gap-3">
                    <span className="font-mono text-eyebrow text-gray-400">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <h3 className="font-display text-display-sm font-medium text-white">
                      {item.title}
                    </h3>
                  </div>
                  <p className="max-w-measure text-gray-300">{item.description}</p>
                </li>
              ))}
            </ol>
          </section>

          <section>
            <h2 className="font-display text-display-sm font-bold text-white sm:text-display-md">
              {story.resultsSection.title}
            </h2>

            <dl className="mt-8 grid gap-x-10 border-t border-gray-800 sm:grid-cols-3">
              {story.resultsSection.highlights.map((item) => (
                <div key={item.label} className="border-b border-gray-800 py-8">
                  <dt className="sr-only">{item.label}</dt>
                  <dd>
                    <span
                      className={`block font-display text-display-md font-bold sm:text-display-lg ${accentText}`}
                    >
                      {item.metric}
                    </span>
                    <span className="mt-3 block font-mono text-eyebrow uppercase text-gray-400">
                      {item.label}
                    </span>
                  </dd>
                </div>
              ))}
            </dl>

            <div className="mt-10 grid gap-x-10 lg:grid-cols-[1fr_2fr]">
              <div aria-hidden="true" />
              <div className={proseClass}>
                <ReactMarkdown>{story.resultsSection.summary}</ReactMarkdown>
              </div>
            </div>
          </section>
        </article>
      </div>

      <CTASection onOpenContactModal={onOpenContactModal} />
    </div>
  );
};

export default SuccessStoryPage; 